/**
 * usePersona Hook
 * AIDLC Administrative Backend
 *
 * Manages the active persona for the navigation system.
 * Persists selection to localStorage so it survives page reloads
 * and browser sessions.
 *
 * Usage:
 *   const { persona, setPersona, personaMeta } = usePersona();
 */

import { useState, useCallback, useMemo } from 'react';
import {
  type Persona,
  PERSONA_META,
  DEFAULT_PERSONA,
  PERSONA_STORAGE_KEY,
  PERSONAS,
} from '../types/persona';

function loadPersistedPersona(): Persona {
  try {
    const stored = localStorage.getItem(PERSONA_STORAGE_KEY);
    if (stored && PERSONAS.includes(stored as Persona)) {
      return stored as Persona;
    }
  } catch {
    // localStorage may be unavailable (SSR, private browsing)
  }
  return DEFAULT_PERSONA;
}

export function usePersona() {
  const [persona, setPersonaState] = useState<Persona>(loadPersistedPersona);

  const setPersona = useCallback((next: Persona) => {
    setPersonaState(next);
    try {
      localStorage.setItem(PERSONA_STORAGE_KEY, next);
    } catch {
      // silent -- localStorage may be unavailable
    }
  }, []);

  const personaMeta = useMemo(() => PERSONA_META[persona], [persona]);

  return { persona, setPersona, personaMeta };
}
